/**
 * Client for GET /api/cognitory/v2/provisioning-setup/access.
 * Backend remains authoritative; exposes only cognitoryId and displayName.
 */

import {
  COGNITORY_V2_PROVISIONING_SETUP_ACCESS_PATH,
  resolveCognitoryApiBaseUrl,
} from "./cognitoryApiBase";
import { resolveCognitoryBearerToken } from "./cognitoryBearerToken";

export type ProvisioningSetupAccessEntry = {
  cognitoryId: string;
  displayName: string;
};

export type ProvisioningSetupAccessClientResult =
  | {
      kind: "resolved";
      requestId: string;
      cognitories: ProvisioningSetupAccessEntry[];
    }
  | {
      kind: "unauthenticated" | "unable_to_verify" | "temporary_failure";
      requestId: string | null;
    };

function isEntry(value: unknown): value is ProvisioningSetupAccessEntry {
  if (!value || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.cognitoryId === "string" &&
    record.cognitoryId.length > 0 &&
    typeof record.displayName === "string" &&
    record.displayName.length > 0
  );
}

export async function fetchProvisioningSetupAccess(input?: {
  apiBaseUrl?: string;
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<ProvisioningSetupAccessClientResult> {
  const fetchImpl = input?.fetchImpl ?? fetch;
  const token =
    input?.bearerToken !== undefined
      ? input.bearerToken
      : await resolveCognitoryBearerToken();
  if (!token) {
    return { kind: "unauthenticated", requestId: null };
  }

  const base = input?.apiBaseUrl ?? resolveCognitoryApiBaseUrl();
  let response: Response;
  try {
    response = await fetchImpl(
      `${base}${COGNITORY_V2_PROVISIONING_SETUP_ACCESS_PATH}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        cache: "no-store",
      },
    );
  } catch {
    return { kind: "temporary_failure", requestId: null };
  }

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  const requestId =
    body &&
    typeof body === "object" &&
    typeof (body as { requestId?: unknown }).requestId === "string"
      ? (body as { requestId: string }).requestId
      : null;

  if (response.status === 401) {
    return { kind: "unauthenticated", requestId };
  }
  if (response.status === 503) {
    return { kind: "temporary_failure", requestId };
  }
  if (response.status !== 200 || !body || typeof body !== "object") {
    return { kind: "unable_to_verify", requestId };
  }

  const decision = (body as { decision?: unknown }).decision;
  if (decision !== "provisioning_setup_access_resolved") {
    return { kind: "unable_to_verify", requestId };
  }

  const rawList = (body as { cognitories?: unknown }).cognitories;
  if (!Array.isArray(rawList)) {
    return { kind: "unable_to_verify", requestId };
  }

  const cognitories: ProvisioningSetupAccessEntry[] = [];
  for (const item of rawList) {
    if (isEntry(item)) {
      cognitories.push({
        cognitoryId: item.cognitoryId,
        displayName: item.displayName,
      });
    }
  }

  return {
    kind: "resolved",
    requestId: requestId ?? "unknown",
    cognitories,
  };
}
